const SNAP_THRESHOLD = 8;
const RESIZE_SNAP_THRESHOLD = 5;

const findClosestTarget = (value, targets, snapDistance) =>
  targets.reduce((best, target) => {
    const distance = Math.abs(value - target.value);
    if (distance <= snapDistance && (best === null || distance < best.distance)) {
      return { distance, value: target.value, guide: target.guide };
    }
    return best;
  }, null);

const edgeTargets = (positions) => positions.map((position) => ({ value: position, guide: position }));

export const snapLayerPosition = ({
  x,
  y,
  width,
  height,
  stageWidth,
  stageHeight,
  threshold = SNAP_THRESHOLD,
  zoom = 1,
  otherElements = [],
}) => {
  let nextX = x;
  let nextY = y;
  const guides = [];
  const snapDistance = threshold / (zoom || 1);

  const halfW = width / 2;
  const halfH = height / 2;

  const xTargets = [
    { value: halfW, guide: 0 },
    { value: stageWidth / 2, guide: stageWidth / 2 },
    { value: stageWidth - halfW, guide: stageWidth },
  ];

  const yTargets = [
    { value: halfH, guide: 0 },
    { value: stageHeight / 2, guide: stageHeight / 2 },
    { value: stageHeight - halfH, guide: stageHeight },
  ];

  otherElements.forEach((el) => {
    if (!el.visible) {
      return;
    }
    xTargets.push(
      { value: el.x, guide: el.x },
      { value: el.x - el.width / 2 + halfW, guide: el.x - el.width / 2 },
      { value: el.x + el.width / 2 - halfW, guide: el.x + el.width / 2 },
    );
    yTargets.push(
      { value: el.y, guide: el.y },
      { value: el.y - el.height / 2 + halfH, guide: el.y - el.height / 2 },
      { value: el.y + el.height / 2 - halfH, guide: el.y + el.height / 2 },
    );
  });

  const bestX = findClosestTarget(x, xTargets, snapDistance);
  const bestY = findClosestTarget(y, yTargets, snapDistance);

  if (bestX) {
    nextX = bestX.value;
    guides.push({ orientation: 'v', position: bestX.guide });
  }

  if (bestY) {
    nextY = bestY.value;
    guides.push({ orientation: 'h', position: bestY.guide });
  }

  return { x: nextX, y: nextY, guides };
};

export const snapResizePoint = ({
  x,
  y,
  snapX,
  snapY,
  stageWidth,
  stageHeight,
  threshold = RESIZE_SNAP_THRESHOLD,
  zoom = 1,
  otherElements = [],
}) => {
  const snapDistance = threshold / (zoom || 1);
  const visibleElements = otherElements.filter((el) => el.visible);
  const guides = [];
  let nextX = x;
  let nextY = y;

  if (snapX) {
    const bestX = findClosestTarget(
      x,
      edgeTargets([
        0,
        stageWidth / 2,
        stageWidth,
        ...visibleElements.flatMap((el) => [el.x - el.width / 2, el.x, el.x + el.width / 2]),
      ]),
      snapDistance,
    );
    if (bestX) {
      nextX = bestX.value;
      guides.push({ orientation: 'v', position: bestX.guide });
    }
  }

  if (snapY) {
    const bestY = findClosestTarget(
      y,
      edgeTargets([
        0,
        stageHeight / 2,
        stageHeight,
        ...visibleElements.flatMap((el) => [el.y - el.height / 2, el.y, el.y + el.height / 2]),
      ]),
      snapDistance,
    );
    if (bestY) {
      nextY = bestY.value;
      guides.push({ orientation: 'h', position: bestY.guide });
    }
  }

  return { x: nextX, y: nextY, guides };
};

export const getElementsBox = (elements) => {
  if (!elements.length) {
    return null;
  }
  const left = Math.min(...elements.map((el) => el.x - el.width / 2));
  const right = Math.max(...elements.map((el) => el.x + el.width / 2));
  const top = Math.min(...elements.map((el) => el.y - el.height / 2));
  const bottom = Math.max(...elements.map((el) => el.y + el.height / 2));
  return { x: (left + right) / 2, y: (top + bottom) / 2, width: right - left, height: bottom - top };
};

export const getCenteredLayerPosition = (layer, stageWidth, stageHeight, mode = 'both') => {
  const next = { x: layer.x, y: layer.y };
  if (mode === 'horizontal' || mode === 'both') {
    next.x = stageWidth / 2;
  }
  if (mode === 'vertical' || mode === 'both') {
    next.y = stageHeight / 2;
  }
  return next;
};
